import { t } from "@lingui/macro";
import Image from "next/image";
import { useRouter } from "next/router";
import styled, { useTheme } from "styled-components";
import useSWR from "swr";

import { useTranslations } from "components/feedback";
import { useTrackerContext } from "context/tracker";
import CallIcon from "icons/call";
import SMSIcon from "icons/sms";
import { LatLngLiteral, Task } from "types/types";
import { formatDateTime, relativeTime } from "utils/datetime-utils";
import { getTaskState, httpFetcher } from "utils/utils";

const Container = styled.div`
  position: fixed;
  z-index: 1;
  bottom: 0;
  margin: ${(props) => props.theme.layout.margin};
  padding: 16px;
  width: 420px;
  border-radius: ${(props) => props.theme.layout.borderRadius};
  background: ${(props) => props.theme.colors.infoBoxBackgroundColor};
  color: ${(props) => props.theme.colors.infoBoxBodyColor};

  @media only screen and (max-width: 768px) {
    z-index: 1;
    width: 100%;
    margin: 0;
    height: max-content;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const Tag = styled.div<{ taskColor: string | undefined }>`
  padding: 4px 6px;
  background: ${(props) => props.taskColor};
  border-radius: 3px;
  margin-bottom: 8px;
  margin-left: 8px;
  color: #fff;

  :first-letter {
    text-transform: capitalize;
  }
`;
const Detail = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  margin-bottom: -10px;
`;

const DetailTitle = styled.div`
  font-size: 18px;
  font-family: CamptonSemiBold, serif;

  :first-letter {
    text-transform: capitalize;
  }
`;

const DetailIcon = styled.div`
  width: 20px;
`;

const DetailDescription = styled.p`
  margin-left: 10px;
  font-size: 16px;
  white-space: nowrap;
  width: 99%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Actions = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
`;

const Action = styled.a`
  margin-top: 20px;
  background: ${(props) => props.theme.colors.infoBoxButtonColor};
  border: none;
  width: 100%;
  border-radius: ${(props) => props.theme.layout.borderRadius};
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  :first-child {
    margin-right: 10px;
  }

  :last-child {
    margin-left: 10px;
  }
`;

const TaskInformation = ({
  activeTask,
  origin,
  activeDestination,
}: {
  activeTask: Task;
  origin: LatLngLiteral | undefined;
  activeDestination: LatLngLiteral | undefined;
}) => {
  const { data: tracker, routingAPI } = useTrackerContext();
  const theme: any = useTheme();
  const router = useRouter();
  const { translatedStatus, translatedCategory } = useTranslations();

  const carrierName = (tracker?.show_driver_info && activeTask.assignee_data?.display_name) || "";
  const name = activeTask.contact.name;
  const originCoordinates = origin;
  const destinationCoordinates = activeDestination;
  const scheduledTime = activeTask.scheduled_time ? activeTask.scheduled_time : null;
  const showETA = tracker?.show_eta;
  const address = (tracker?.show_destination && activeTask.address.formatted_address) || "";
  const txtMsg = (tracker?.show_sms_action && activeTask.assignee_data?.phone) || "";
  const phoneNo = (tracker?.show_call_action && activeTask.assignee_data?.phone) || "";
  const url =
    showETA &&
    routingAPI &&
    originCoordinates &&
    destinationCoordinates &&
    `${routingAPI}/${originCoordinates.lng},${originCoordinates.lat};${destinationCoordinates.lng},${destinationCoordinates.lat}?alternatives=false&steps=true&geometries=polyline`;

  const { data } = useSWR(url, httpFetcher(router.locale)); // ignore error as response is validated

  const eta = data && data.routes ? data.routes[0] && data.routes[0].duration : 0;

  return (
    <Container>
      <Detail>
        <DetailTitle>{translatedCategory[activeTask?.category]}</DetailTitle>
        <Tag taskColor={getTaskState(activeTask?.state)?.color}>
          {theme?.text?.status?.[activeTask.state] || translatedStatus[activeTask.state]}
        </Tag>
      </Detail>
      {address && (
        <Detail>
          <DetailIcon>
            <Image src={theme.icons.location} alt={"location"} width={20} height={20} />
          </DetailIcon>
          <DetailDescription title={address}>{address}</DetailDescription>
        </Detail>
      )}
      {scheduledTime && theme.layout.showScheduledTime !== false && (
        <Detail>
          <DetailIcon>
            <Image src={theme.icons.time} alt={"schedule-time"} width={20} height={20} />
          </DetailIcon>
          <DetailDescription>
            {t`Scheduled time:`} {formatDateTime(scheduledTime.toString())}
          </DetailDescription>
        </Detail>
      )}
      {showETA && (
        <Detail>
          <DetailIcon>
            <Image src={theme.icons.time} alt={"eta"} width={20} height={20} />
          </DetailIcon>
          <DetailDescription>
            {t`Estimated time:`} {relativeTime(eta)}
          </DetailDescription>
        </Detail>
      )}
      {carrierName && (
        <Detail>
          <DetailIcon>
            <Image src={theme.icons.carrier} alt={"carrier"} width={20} height={20} />
          </DetailIcon>
          <DetailDescription>{carrierName}</DetailDescription>
        </Detail>
      )}
      {name && (
        <Detail>
          <DetailIcon>
            <Image src={theme.icons.contact} alt={"contact"} width={20} height={20} />
          </DetailIcon>
          <DetailDescription>{name}</DetailDescription>
        </Detail>
      )}
      <Actions>
        {phoneNo && (
          <Action href={`tel:${phoneNo}`}>
            <CallIcon />
          </Action>
        )}
        {txtMsg && (
          <Action href={`sms:${txtMsg}`}>
            <SMSIcon />
          </Action>
        )}
      </Actions>
    </Container>
  );
};

export default TaskInformation;
